/**
 * Simple keyboard shortcut handler using VueUse's onKeyStroke
 */

import { onKeyStroke } from '@vueuse/core';
import { ref } from 'vue';

// Active contexts
const activeContexts = ref(new Set<string>());

/**
 * Check if the event target is an editable element
 */
function isEditableElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  const isInput = tagName === 'input';
  const isTextarea = tagName === 'textarea';
  const isSelect = tagName === 'select';
  const isContentEditable = target.isContentEditable;

  if (isInput) {
    const inputType = (target as HTMLInputElement).type?.toLowerCase();
    const allowedInputTypes = ['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'image'];
    if (inputType && allowedInputTypes.includes(inputType)) {
      return false;
    }
    return true;
  }

  return isTextarea || isSelect || isContentEditable;
}

/**
 * Register a keyboard shortcut
 */
export function useKeyboardShortcut(
  key: string | string[],
  handler: (event: KeyboardEvent) => void,
  options?: {
    context?: string;
    allowInInputs?: boolean;
    modifiers?: ('meta' | 'ctrl' | 'shift' | 'alt')[];
  },
) {
  const context = options?.context;
  const allowInInputs = options?.allowInInputs ?? false;
  const modifiers = options?.modifiers;

  return onKeyStroke(key, (event) => {
    // Check context (global is always active)
    if (context && context !== 'global') {
      if (!activeContexts.value.has(context)) {
        return;
      }
    }

    // Check modifiers
    if (modifiers) {
      // For meta+ctrl, accept either one (cross-platform)
      const needsMetaOrCtrl = modifiers.includes('meta') && modifiers.includes('ctrl');
      if (needsMetaOrCtrl) {
        if (!event.metaKey && !event.ctrlKey) {
          return;
        }
      } else {
        // Check each required modifier
        if (modifiers.includes('meta') && !event.metaKey) return;
        if (modifiers.includes('ctrl') && !event.ctrlKey) return;
        if (modifiers.includes('shift') && !event.shiftKey) return;
        if (modifiers.includes('alt') && !event.altKey) return;
      }

      // Ensure no extra modifiers are pressed (unless shift is part of the key itself)
      const keyLower = event.key.toLowerCase();
      const isLetterKey = /^[a-z]$/.test(keyLower);
      if (!isLetterKey && event.shiftKey && !modifiers.includes('shift')) return;
      if (needsMetaOrCtrl) {
        // Allow both meta and ctrl to be pressed
      } else {
        if (event.metaKey && !modifiers.includes('meta')) return;
        if (event.ctrlKey && !modifiers.includes('ctrl')) return;
      }
      if (event.altKey && !modifiers.includes('alt')) return;
    } else {
      // If no modifiers specified, ensure NO modifiers are pressed
      // Exception: allow shift for letter keys (it's part of the key itself)
      const keyLower = event.key.toLowerCase();
      const isLetterKey = /^[a-z]$/.test(keyLower);
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      // For non-letter keys, also check shift
      if (!isLetterKey && event.shiftKey) {
        return;
      }
    }

    // Block shortcuts in editable elements (unless allowed)
    if (!allowInInputs && isEditableElement(event.target)) {
      return;
    }

    // Prevent default for arrow keys to avoid page scrolling
    // Only prevent if we're actually handling the shortcut
    const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
    if (isArrowKey) {
      event.preventDefault();
    }

    handler(event);
  });
}

/**
 * Activate a context
 */
export function activateContext(context: string): void {
  activeContexts.value.add(context);
}

/**
 * Deactivate a context
 */
export function deactivateContext(context: string): void {
  activeContexts.value.delete(context);
}
